import React, { useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import StatusBadge from "@/shared/components/StatusBadge";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useCancelPathPickOrder from "@/features/pathpick/features/order-history/hooks/mutation/useCancelPathPickOrder";
import PathPickOrderCancelModal from "@/features/pathpick/features/order-history/features/detail/components/overview-section/PathPickOrderCancelModal";
import {
  DEFAULT_ORDER_STATUS_COLOR_VARIANT,
  ORDER_STATUS_META,
} from "@/features/pathpick/features/order-history/constants";
import {
  ORDER_CANCEL_TOAST_ID,
  ORDER_DETAIL_KEY,
} from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrder } from "@/features/pathpick/features/order-history/types/response";
import { formatPathPickOrderDateTime } from "@/features/pathpick/features/order-history/utils";

interface Props {
  order: PathPickOrder;
}

// 주문 상태 배지 + 주문번호/주문일시, 그리고 우측의 주문 취소 액션.
// "주문 상세" 제목과 뒤로가기는 페이지 셸의 PageHeader가 담당한다.
function PathPickOrderDetailHeader({ order }: Props) {
  const { t } = useI18nHelper();
  // 매핑되지 않은 상태가 내려오면 원문 status를 그대로 노출한다(목록과 동일한 폴백).
  const statusMeta = ORDER_STATUS_META[order.status];
  const statusLabel = statusMeta ? t(statusMeta.labelKey) : order.status;
  const statusColorVariant =
    statusMeta?.colorVariant ?? DEFAULT_ORDER_STATUS_COLOR_VARIANT;

  // 입금 전에만 바이어가 직접 취소할 수 있다 — 이후 상태는 서버도 막는다.
  const isCancelable = order.status === "REMITTANCE_REQUESTED";

  return (
    // 좁은 폭에서는 버튼이 아래로 내려가되, 배지가 컬럼 폭만큼 늘어나지는 않게 flex-start.
    <Flex
      gap={"12px"}
      width={"100%"}
      flexWrap={"wrap"}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Flex flexDirection={"column"} gap={"8px"} alignItems={"flex-start"}>
        <StatusBadge status={statusLabel} colorVariant={statusColorVariant} />
        <Flex gap={"8px"} alignItems={"center"}>
          <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
            {order.orderNumber}
          </Text>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {t(`${ORDER_DETAIL_KEY}ordered_at`, {
              datetime: formatPathPickOrderDateTime(order.orderedAt),
            })}
          </Text>
        </Flex>
      </Flex>
      {isCancelable && <PathPickOrderDetailCancelAction order={order} />}
    </Flex>
  );
}

// 취소 액션만 따로 떼어 둔다 — 취소할 수 없는 주문에서는 mutation 훅 자체를 걸지 않기 위해서다.
function PathPickOrderDetailCancelAction({ order }: { order: PathPickOrder }) {
  const { t } = useI18nHelper();
  const { info } = useAppToasts({ id: "GLOBAL" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: cancelOrder, isPending: isCanceling } =
    useCancelPathPickOrder(order.id);

  return (
    <>
      <AppButton
        variant={"white"}
        onClick={() => setIsModalOpen(true)}
        chakraProps={{ flexShrink: 0 }}
      >
        <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
          {t(`${ORDER_DETAIL_KEY}cancel_order_button`)}
        </Text>
      </AppButton>

      <PathPickOrderCancelModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        orderNumber={order.orderNumber}
        isCanceling={isCanceling}
        onConfirm={(cancelReason) =>
          cancelOrder(
            { cancelReason },
            {
              onSuccess: () => {
                setIsModalOpen(false);
                // 성공하면 이 컴포넌트가 곧 언마운트되므로 공용 "GLOBAL" id로 띄우면
                // 언마운트 cleanup에 지워진다 — 전용 id로 띄워야 토스트가 남는다.
                info(
                  t(`${ORDER_DETAIL_KEY}cancel_success`),
                  ORDER_CANCEL_TOAST_ID
                );
              },
            }
          )
        }
      />
    </>
  );
}

export default PathPickOrderDetailHeader;
